import { useState } from 'react';
import { useGallery } from '../hooks/useGallery';
import { useGallerySelection } from '../hooks/useGallerySelection';
import { usePendingVideoPoller } from '../hooks/usePendingVideoPoller';
import { useMediaDownload } from '../hooks/useMediaDownload';
import type { DownloadableItem } from '../hooks/useMediaDownload';
import { GalleryToolbar } from './GalleryToolbar';
import { LoadMoreSentinel } from './LoadMoreSentinel';
import { apiJson } from '../api/client';

const API_BASE = import.meta.env.VITE_API_BASE as string;
const ENDPOINT = '/api/tools/videos';

interface VideoItem {
  id: string;
  title: string | null;
  prompt: string;
  tool_name: string;
  model: string;
  duration_seconds: number | null;
  aspect_ratio: string | null;
  resolution: string | null;
  status: 'submitted' | 'in_progress' | 'ready' | 'failed' | string;
  error_message: string | null;
  r2_key: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  koji_codename: string | null;
  turn_id: string | null;
  created_at: number;
  completed_at: number | null;
  signedUrl: string | null;
}

function absolute(url: string): string {
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'submitted':
      return 'Queued';
    case 'in_progress':
      return 'Generating…';
    case 'ready':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

export function VideoGallery({
  videoGenEnabled = true,
}: {
  videoGenEnabled?: boolean;
} = {}) {
  const { items, loading, error, deleteItem, refresh, loadMore, hasMore } =
    useGallery<VideoItem>(ENDPOINT);
  const selection = useGallerySelection(ENDPOINT);
  const download = useMediaDownload({
    zipEndpoint: '/api/tools/videos/zip',
    kind: 'video',
  });
  const [active, setActive] = useState<VideoItem | null>(null);
  // Stage 42: retry state mirrors ImageGallery's pattern.
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);

  usePendingVideoPoller(refresh);

  async function retryVideo(id: string): Promise<void> {
    setRetryError(null);
    setRetryingId(id);
    try {
      await apiJson<{ retried: string; handler: string; provider: string }>(
        'POST',
        `/api/tools/videos/${encodeURIComponent(id)}/retry`,
      );
      // Flip the card to "Queued" immediately. The pending-video poller
      // then picks up subsequent state transitions automatically.
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setRetryError(`Retry failed for ${id}: ${msg}`);
    } finally {
      setRetryingId((current) => (current === id ? null : current));
    }
  }

  const allIds = items.map((v) => v.id);

  const selectedDownloadItems: DownloadableItem[] = items
    .filter((v) => selection.selected.has(v.id))
    .map((v) => ({
      id: v.id,
      signedUrl: v.signedUrl,
      status: v.status,
      title: v.title,
      koji_codename: v.koji_codename,
      mime_type: v.mime_type ?? 'video/mp4',
    }));

  return (
    <section className="gallery gallery-videos">
      {/* Stage 27: when video generation is currently disabled in Settings,
          the gallery stays accessible so users can still play and download
          existing clips. No +New button exists (videos are created via the
          kling-video-tool function call, not a manual form). */}
      {!videoGenEnabled ? (
        <div
          role="status"
          aria-live="polite"
          className="gallery-banner-info"
          style={{
            padding: '8px 12px',
            marginBottom: 12,
            background: 'var(--bg-subtle, #1a1a1a)',
            border: '1px solid var(--border, #333)',
            borderRadius: 6,
            fontSize: '0.9em',
          }}
        >
          ⓘ Video generation is currently disabled in Settings → Permissions.
          Existing clips remain available for playback and download.
        </div>
      ) : null}
      <GalleryToolbar
        selection={selection}
        allIds={allIds}
        download={{
          selectedItems: selectedDownloadItems,
          downloadOne: download.downloadOne,
          downloadZip: download.downloadZip,
          busy: download.busy,
          error: download.error,
        }}
      />
      {/* Stage 42: surface retry errors inline above the grid. */}
      {retryError ? (
        <div className="status-missing small" role="alert">
          {retryError}
        </div>
      ) : null}
      {loading && items.length === 0 ? (
        <div className="muted">Loading videos…</div>
      ) : error ? (
        <div className="status-missing">Error: {error}</div>
      ) : items.length === 0 ? (
        <p className="muted gallery-empty">
          No videos yet. Ask the assistant to generate one — e.g. “make a
          short cinematic clip of a forest at dawn”.
        </p>
      ) : (
        <div className="gallery-grid">
          {items.map((vid) => {
            const isSelected = selection.selected.has(vid.id);
            const isReady = vid.status === 'ready' && vid.signedUrl;
            const isFailed = vid.status === 'failed';
            const isPending =
              vid.status === 'submitted' || vid.status === 'in_progress';
            const displayTitle = vid.title || vid.koji_codename || vid.id;

            return (
              <div
                key={vid.id}
                className={
                  'gallery-card gallery-card-video' +
                  (isSelected ? ' gallery-card-selected' : '') +
                  (isPending ? ' gallery-card-pending' : '') +
                  (isFailed ? ' gallery-card-failed' : '')
                }
              >
                {selection.selectMode ? (
                  <label className="gallery-select-label">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => selection.toggleSelected(vid.id)}
                      aria-label={`Select ${displayTitle}`}
                    />
                  </label>
                ) : null}

                {isReady ? (
                  <button
                    type="button"
                    className="gallery-thumb-btn"
                    onClick={() => {
                      if (selection.selectMode) {
                        selection.toggleSelected(vid.id);
                      } else {
                        setActive(vid);
                      }
                    }}
                  >
                    <video
                      src={absolute(vid.signedUrl as string)}
                      className="gallery-thumb"
                      preload="metadata"
                      muted
                      playsInline
                    />
                    <div className="gallery-video-play-overlay" aria-hidden="true">
                      ▶
                    </div>
                  </button>
                ) : (
                  <div className="gallery-thumb gallery-thumb-placeholder">
                    <div className="gallery-video-status-badge">
                      {statusLabel(vid.status)}
                    </div>
                    {isPending ? (
                      <div className="muted small" style={{ marginTop: 6 }}>
                        ~1–3 min
                      </div>
                    ) : null}
                  </div>
                )}

                <div className="gallery-meta">
                  <div className="gallery-title-row">
                    <span className="gallery-title" title={vid.prompt}>
                      {displayTitle}
                    </span>
                    {/* Stage 41: source-tool badge so users can tell at a
                        glance whether a card was produced by Veo or Kling. */}
                    {vid.tool_name === 'veo-video-tool' ? (
                      <span
                        className="gallery-source-badge gallery-source-badge-veo"
                        title="Generated with Google Veo 3.1 Fast"
                        aria-label="Veo"
                      >
                        veo
                      </span>
                    ) : null}
                    {vid.tool_name === 'kling-video-tool' ? (
                      <span
                        className="gallery-source-badge gallery-source-badge-kling"
                        title="Generated with Kling v1"
                        aria-label="Kling"
                      >
                        kling
                      </span>
                    ) : null}
                  </div>
                  <div className="muted small">
                    {vid.duration_seconds ? `${vid.duration_seconds}s` : '—'}
                    {vid.aspect_ratio ? ` · ${vid.aspect_ratio}` : ''}
                    {vid.resolution ? ` · ${vid.resolution}` : ''}
                    {vid.koji_codename ? ` · ${vid.koji_codename}` : ''}
                  </div>
                  {isFailed && vid.error_message ? (
                    <div className="status-missing small" title={vid.error_message}>
                      {vid.error_message.slice(0, 80)}
                      {vid.error_message.length > 80 ? '…' : ''}
                    </div>
                  ) : null}
                </div>

                {!selection.selectMode ? (
                  <div className="gallery-card-actions">
                    {/* Stage 42: retry button on failed cards. Re-submits
                        the original prompt + parameters via the
                        appropriate provider; the existing card stays in
                        place (in-place row reset) and transitions through
                        the lifecycle again. */}
                    {isFailed ? (
                      <button
                        className="btn-secondary small"
                        disabled={retryingId === vid.id}
                        onClick={() => void retryVideo(vid.id)}
                        title="Re-queue this video with the original prompt and parameters"
                      >
                        {retryingId === vid.id ? 'Retrying…' : '↻ Retry'}
                      </button>
                    ) : null}
                    <button
                      className="btn-danger small"
                      onClick={() => {
                        if (confirm('Delete this video?')) {
                          void deleteItem(vid.id);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {active ? (
        <div
          className="lightbox-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setActive(null);
          }}
        >
          <div className="lightbox-video-wrap">
            <video
              src={active.signedUrl ? absolute(active.signedUrl) : undefined}
              className="lightbox-video"
              controls
              autoPlay
              playsInline
            />
            <div className="lightbox-video-meta">
              <div className="lightbox-video-title">
                {active.title || active.koji_codename || active.id}
              </div>
              <div className="muted small">{active.prompt}</div>
              <div className="row" style={{ marginTop: 8 }}>
                {active.signedUrl ? (
                  <a
                    className="btn-primary"
                    href={absolute(active.signedUrl)}
                    download={`${active.koji_codename ?? active.id}.mp4`}
                  >
                    Download
                  </a>
                ) : null}
                <button onClick={() => setActive(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <LoadMoreSentinel
        hasMore={hasMore}
        loading={loading}
        onReach={() => void loadMore()}
      />
    </section>
  );
}
